import { useState, type FormEvent } from 'react';
import type { AdminCatalogItem, CatalogItemInput, MediaItem } from '../../lib/types';
import { MediaPicker } from './MediaPicker';

function slugify(value: string) { return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }
function priceText(cents: number | null) { return cents === null ? '' : (cents / 100).toFixed(2); }
function itemValue(item?: AdminCatalogItem): { input: Omit<CatalogItemInput, 'priceCents'>; price: string } {
  return item
    ? { input: { slug: item.slug, name: item.name, sku: item.sku, summary: item.summary, description: item.description, imageId: item.imageId, status: item.status, sortOrder: item.sortOrder }, price: priceText(item.priceCents) }
    : { input: { slug: '', name: '', sku: '', summary: '', description: '', imageId: null, status: 'draft', sortOrder: 0 }, price: '' };
}

export function CatalogForm({ item, media, pending = false, onSubmit, onCancel }: { item?: AdminCatalogItem; media: MediaItem[]; pending?: boolean; onSubmit: (value: CatalogItemInput) => void; onCancel?: () => void }) {
  const initial = itemValue(item);
  const [input, setInput] = useState(initial.input);
  const [price, setPrice] = useState(initial.price);
  function update<Key extends keyof typeof input>(key: Key, next: (typeof input)[Key]) { setInput((current) => ({ ...current, [key]: next })); }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const priceCents = price.trim() === '' ? null : Math.round(Number(price) * 100);
    onSubmit({ ...input, slug: input.slug || slugify(input.name), priceCents });
  }
  return (
    <form className="admin-form" onSubmit={submit}>
      <div className="admin-form-grid">
        <label>Name<input required value={input.name} onChange={(event) => { update('name', event.target.value); if (!item && !input.slug) update('slug', slugify(event.target.value)); }} /></label>
        <label>Path<input required value={input.slug} onChange={(event) => update('slug', slugify(event.target.value))} /></label>
        <label>SKU<input value={input.sku} onChange={(event) => update('sku', event.target.value)} /></label>
        <label>Price (USD)<input inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} /></label>
        <label className="admin-form-wide">Summary<textarea rows={2} value={input.summary} onChange={(event) => update('summary', event.target.value)} /></label>
        <label className="admin-form-wide">Description<textarea rows={6} value={input.description} onChange={(event) => update('description', event.target.value)} /></label>
        <MediaPicker media={media} value={input.imageId} onChange={(id) => update('imageId', id)} />
        <label>Publication<select value={input.status} onChange={(event) => update('status', event.target.value as CatalogItemInput['status'])}><option value="draft">Draft</option><option value="published">Published</option></select></label>
        <label>Sort order<input type="number" value={input.sortOrder} onChange={(event) => update('sortOrder', Number(event.target.value))} /></label>
      </div>
      <div className="admin-form-actions">
        <button disabled={pending}>{pending ? 'Saving…' : 'Save item'}</button>
        {onCancel && <button className="secondary" type="button" onClick={onCancel}>Cancel</button>}
      </div>
    </form>
  );
}
